import React, { useCallback, useMemo, useState } from "react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import {
  FileText,
  Download,
  Eye,
  Search,
  Filter,
  BarChart3,
  User,
  Plus,
  Edit2,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Folder as FolderIcon,
  Settings,
  CheckCircle,
  Trash2,
  X,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import NewDocumentModal, {
  NewDocumentData,
  UpdateDocumentData,
} from "@/components/ui/NewDocumentModal";
import QuizModal from "@/components/ui/QuizModal";
import DocumentSkeleton from "./DocumentSkeleton";
import MyDocumentsSkeleton from "./MyDocumentsSkeleton";
import ViewDocumentModal from "../ui/ViewDocumentModal";
import { DocumentItem, GeneratedQuiz, useDocumentsData } from "./documentsData";
import { getCompanyDocumentsByCompanyIdAndEmail } from "@/dispatchers/companyDocumentsDispatchers";
import { Skeleton } from "@mui/material";
import { Row } from "react-day-picker";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseDocumentsUpsert,
  accessTitanverseFullAccess,
} from "@/data/constantStrings";
import { toast } from "@/components/ui/use-toast";

export default function Documents({
  onStartSession,
}: {
  onStartSession?: (docs: DocumentItem[], initialId: string) => void;
}) {
  const dataHook = useDocumentsData() || ({} as any);
  const {
    handleCreateDocument,
    isCompanyDocumentsLoading,
    darkMode,
    companyDocuments,
    currentUser,
    companyId,
  } = dataHook;
  if (isCompanyDocumentsLoading) {
    return <DocumentSkeleton isDarkMode={!!darkMode} />;
  }

  return (
    <DocumentsContent
      onStartSession={onStartSession}
      handleCreateDocument={handleCreateDocument}
      darkMode={!!darkMode}
      pharmacyDocuments={companyDocuments}
      updateDocument={dataHook.updateDocument}
      saveQuizToDocument={dataHook.saveQuizToDocument}
      removeQuizFromDocument={dataHook.removeQuizFromDocument}
      markDocumentsReviewed={dataHook.markDocumentsReviewed}
      currentUserEmail={dataHook.currentUserEmail}
      currentUser={currentUser}
    />
  );
}

const formatUKDateTime = (d: string | number | Date) =>
  new Date(d).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const displayNameFromEmail = (email: string) => {
  const local = (email || "").split("@")[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// Review interval utilities (module scope so memoized rows can use them)
export const toMonths = (s?: string): number | null => {
  if (!s) return null;
  const v = s.trim().toLowerCase();
  if (v === "monthly" || v === "month") return 1;
  if (v === "quarterly" || v === "quarter") return 3;
  if (v === "biannual" || v === "semiannual" || v === "6 months") return 6;
  if (v === "annual" || v === "annually" || v === "yearly" || v === "year")
    return 12;
  const m = v.match(/(\d+)\s*(month|months|mon|m)/);
  if (m) return Math.max(1, parseInt(m[1], 10));
  const y = v.match(/(\d+)\s*(year|years|y)/);
  if (y) return Math.max(1, parseInt(y[1], 10)) * 12;
  return null;
};
export const addMonths = (date: Date, months: number) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

function DocumentsContent({
  onStartSession,
  handleCreateDocument,
  darkMode: isDarkMode,
  pharmacyDocuments,
  updateDocument,
  saveQuizToDocument,
  removeQuizFromDocument,
  markDocumentsReviewed,
  currentUserEmail,
  currentUser,
}: {
  onStartSession?: (docs: DocumentItem[], initialId: string) => void;
  handleCreateDocument?: (payload: any) => void;
  darkMode?: boolean;
  pharmacyDocuments?: DocumentItem[];
  updateDocument?: (id: string, data: UpdateDocumentData) => void;
  saveQuizToDocument?: (id: string, quiz: GeneratedQuiz) => void;
  removeQuizFromDocument?: (id: string) => void;
  markDocumentsReviewed?: (ids: string[], reviewerEmail: string) => void;
  currentUserEmail?: string;
  currentUser?: any;
}) {
  const {
    handleDocumentTabsClick,
    activeDocumentsTab,
    setActiveDocumentsTab,
    isDocumentsAnalyticsLoading,
    documentsAnalytics,
    isDocumentsOverallComplianceLoading,
    documentsOverallCompliance,
    isDocumentsMembersComplianceLoading,
    documentsMembersCompliance,
    fetchDocumentById,
    downloadDocumentById,
    companyFolders,
    handleGetFolders,
    handleAddFolder,
    handleRenameFolder,
    handleDeleteEmptyFolders,
    getCompanyDocumentByIdData,
    enumToDocumentType,
    enumToReviewInterval,
    mapEnumToDocumentType,
    mapEnumToFolderName,
    mapEnumToReviewInterval,
    handleUpdateDocument,
    setViewingDoc,
    viewingDoc,
    myDocuments,
    isMyDocumentsLoading,
    myDocumentsData,
    currentUserEmail: hookCurrentUserEmail,
    companyDocuments,
    handleReview,
    setFolder,
    handleDeleteCompanyDocuments,
    companyId,
  } = useDocumentsData();

  const [query, setQuery] = useState("");
  const [type, setType] = useState<string>("all");
  const [memberFilter, setMemberFilter] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState<{
    id: string;
    title: string;
    type: DocumentItem["type"];
    assignedEmails: string[];
    reviewInterval?: string;
    folder?: string;
    quiz?: DocumentItem["quiz"];
    roles: [];
  } | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizDoc, setQuizDoc] = useState<DocumentItem | null>(null);
  const [folders, setFolders] = useState<string[]>([]);
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [selectedEmptyFolderIds, setSelectedEmptyFolderIds] = useState<
    Set<string>
  >(new Set());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const reviewerEmail = currentUserEmail || "";

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState("");
  const [renameFolderName, setRenameFolderName] = useState("");
  const [isRenameSaving, setIsRenameSaving] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderName, setCreateFolderName] = useState("");
  const [createFolderError, setCreateFolderError] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  const hasRolePermission =
    currentUser?.role?.roleName?.toLowerCase() === "admin" ||
    currentUser?.role?.roleName?.toLowerCase() === "superadmin" ||
    checkUserPermissions(accessTitanverseFullAccess);

  const getFolderKey = (folderName: string) => folderName.trim().toLowerCase();

  const folderByName = useMemo(() => {
    const map = new Map<string, any>();
    (companyFolders || []).forEach((folder: any) => {
      const name = folder?.folder || folder?.folderName || folder?.name;
      if (name) map.set(getFolderKey(name), folder);
    });
    return map;
  }, [companyFolders]);

  React.useEffect(() => {
    if (
      !hasRolePermission &&
      (activeDocumentsTab === "admin" || activeDocumentsTab === "analytics")
    ) {
      handleDocumentTabsClick("my");
    }
  }, [hasRolePermission, activeDocumentsTab, handleDocumentTabsClick]);

  React.useEffect(() => {
    if (companyId) {
      handleGetFolders?.();
    }
  }, [companyId]);

  // Derive folders from current documents
  const computedFolders = useMemo(() => {
    const fromDocs = Array.from(
      new Set(
        (pharmacyDocuments || [])
          .map((d) => d.folder)
          .filter(Boolean) as string[],
      ),
    );
    return Array.from(new Set([...(folders || []), ...fromDocs])).sort();
  }, [pharmacyDocuments, folders]);

  // Keep local folders list in sync with derived
  // React.useEffect(() => {
  //   setFolders(computedFolders);
  // }, [computedFolders]);

  const baseDocuments = useMemo(() => {
    const list = pharmacyDocuments || [];
    return list
      .filter((d) =>
        type === "all" ? true : d.type.toLowerCase() === type.toLowerCase(),
      )
      .filter((d) =>
        activeDocumentsTab === "admin" && memberFilter !== "all"
          ? d.assignments.some((a) => a.email === memberFilter) // match by email
          : true,
      )
      .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
  }, [pharmacyDocuments, type, memberFilter, activeDocumentsTab]);

  const documents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return baseDocuments;
    return baseDocuments.filter((d) => {
      const folderName = (d.folder || d.type || "").toLowerCase();
      return (
        (d.title || "").toLowerCase().includes(q) ||
        (d.type || "").toLowerCase().includes(q) ||
        folderName.includes(q) ||
        new Date(d.createdAt).toLocaleDateString("en-GB").includes(q)
      );
    });
  }, [baseDocuments, query]);

  const myAssignedDocuments = useMemo(() => {
    const email = reviewerEmail;
    if (!email) return [] as DocumentItem[];
    return documents.filter((d) =>
      d.assignments.some((a) => a.email === email),
    );
  }, [documents, reviewerEmail]);

  const analytics = useMemo(() => {
    const totalDocs = documents.length;
    let totalAssignments = 0;
    let compliantAssignments = 0;
    const memberSet = new Set<string>();

    const folderMap = new Map<
      string,
      { assigned: number; compliant: number }
    >();

    const now = new Date();
    let overdueReviews = 0;
    let dueNext30 = 0;

    const riskyDocs: {
      id: string;
      title: string;
      folder: string;
      assigned: number;
      compliant: number;
      rate: number;
    }[] = [];

    for (const d of documents) {
      totalAssignments += d.assignments.length;
      const comp = d.assignments.filter((a) => a.compliant).length;
      compliantAssignments += comp;
      for (const a of d.assignments) memberSet.add(a.email);

      const key = d.folder || d.type;
      const f = folderMap.get(key) || { assigned: 0, compliant: 0 };
      f.assigned += d.assignments.length;
      f.compliant += comp;
      folderMap.set(key, f);

      const months = toMonths(d.reviewInterval);
      const last = new Date(d.lastReviewedAt || d.createdAt);
      const next = months ? addMonths(last, months) : null;
      if (next) {
        if (+next < +now) overdueReviews += 1;
        else if (+next - +now <= 30 * 24 * 60 * 60 * 1000) dueNext30 += 1;
      }

      const rate = d.assignments.length
        ? Math.round((comp / d.assignments.length) * 100)
        : 0;
      riskyDocs.push({
        id: d.id,
        title: d.title,
        folder: key,
        assigned: d.assignments.length,
        compliant: comp,
        rate,
      });
    }

    const overallRate = totalAssignments
      ? Math.round((compliantAssignments / totalAssignments) * 100)
      : 0;

    const folders = Array.from(folderMap.entries())
      .map(([folder, s]) => ({
        folder: folder.split(" ")[0],
        ...s,
        rate: s.assigned ? Math.round((s.compliant / s.assigned) * 100) : 0,
      }))
      .sort((a, b) => a.folder.localeCompare(b.folder));

    riskyDocs.sort((a, b) => a.rate - b.rate || b.assigned - a.assigned);

    return {
      totalDocs,
      totalAssignments,
      compliantAssignments,
      overallRate,
      membersCount: memberSet.size,
      overdueReviews,
      dueNext30,
      folders,
      riskyDocs: riskyDocs.slice(0, 5),
    };
  }, [documents]);

  const overallRateCalculated = (
    (documentsOverallCompliance?.compliant /
      (documentsOverallCompliance?.compliant +
        documentsOverallCompliance?.pending)) *
    100
  ).toFixed(1);

  const groupedByType = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, DocumentItem[]>();
    (companyFolders || []).forEach((f: any) => {
      if (f?.folder && !map.has(f.folder)) map.set(f.folder, []);
    });
    for (const d of baseDocuments) {
      const key = d.folder || d.type;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    const matchingIds = new Set(documents.map((d) => d.id));
    return Array.from(map.entries())
      .map(([name, docs]) => {
        if (!q) return [name, docs] as [string, DocumentItem[]];
        if (name.toLowerCase().includes(q))
          return [name, docs] as [string, DocumentItem[]];
        return [name, docs.filter((d) => matchingIds.has(d.id))] as [
          string,
          DocumentItem[],
        ];
      })
      .filter(([name, docs]) => !q || docs.length > 0 || name.toLowerCase().includes(q))
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [baseDocuments, documents, companyFolders, query]);

  const filteredMyDocuments = useMemo(() => {
    const rawDocs = myDocumentsData?.assignedDocuments || [];
    return rawDocs
      .filter((d) =>
        type === "all"
          ? true
          : mapEnumToDocumentType(d.documentType)?.toLowerCase() ===
            type.toLowerCase(),
      )
      .filter((d) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return (
          d.title.toLowerCase().includes(q) ||
          (d.folderName || "").toLowerCase().includes(q) ||
          mapEnumToDocumentType(d.documentType)
            ?.toLowerCase()
            .includes(q) ||
          new Date(d.createdOn).toLocaleDateString("en-GB").includes(q)
        );
      })
      .sort((a, b) => +new Date(b.createdOn) - +new Date(a.createdOn));
  }, [myDocumentsData?.assignedDocuments, query, type, mapEnumToDocumentType]);

  const groupedByTypeForMyDocuments = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const d of filteredMyDocuments) {
      const key =
        d.folderName || mapEnumToDocumentType(d.documentType) || "General";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredMyDocuments, mapEnumToDocumentType]);

  const handleView = async (doc: any) => {
    try {
      setViewLoading(true);

      if (!doc?._id || !doc?.mainFile?.fileName) {
        console.warn("❌ No main file found for this document:", doc);
        setViewingDoc(null);
        return;
      }

      const details = await fetchDocumentById(
        doc._id,
        doc.mainFile.fileName,
        1,
      );

      if (details?.base64Content?.startsWith("data:")) {
        const mimeType = details.base64Content
          .split(",")[0]
          .split(":")[1]
          .split(";")[0];
        const base64Data = details.base64Content.split(",")[1];

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);

        setViewingDoc({
          title: details.fileName,
          url: blobUrl,
          mimeType: mimeType,
        });
        setViewOpen(true);
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      setViewingDoc(null);
    } finally {
      setViewLoading(false);
    }
  };
  const handleDownload = (doc: DocumentItem) => {
    const blob = new Blob([doc.content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.title.replace(/\s+/g, "-").toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCreate = (data: NewDocumentData) => {
    handleCreateDocument?.(data);
    setActiveDocumentsTab("admin");
  };

  const openEditCb = useCallback(async (doc: DocumentItem) => {
    try {
      const res = await getCompanyDocumentByIdData(doc._id);
      if (!res) return;

      // Map backend numeric values to readable text
      const typeLabel = enumToDocumentType[res.documentType] || "General";
      const reviewLabel = enumToReviewInterval[res.reviewInterval] || "Never";
      setEditInitial({
        id: res._id,
        title: res.title,
        type: typeLabel,
        assignedEmails:
          res.assignedToMembers?.map((m: any) => m.emailAddress) || [],
        reviewInterval: reviewLabel,
        folder: res.folderId || "",
        quiz: res.questions ? { questions: res.questions } : undefined,
        mainFile: res.mainFile || null,
        supportingFiles: res.supportingFiles || [],
        roles: res.assignedToRoles || [],
      } as any);

      setEditOpen(true);
    } catch (error) {
      console.error("Error opening edit modal:", error);
    }
  }, []);

  const onToggleExpand = useCallback((id: string) => {
    setExpandedDocs((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const onToggleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleUpdate = (id: string, data: UpdateDocumentData) => {
    updateDocument?.(id, data);
    setEditOpen(false);
    setActiveDocumentsTab("admin");
  };

  const openQuizCb = useCallback((doc: DocumentItem) => {
    setQuizDoc(doc);
    setQuizOpen(true);
  }, []);

  const handleSaveQuiz = (quiz: GeneratedQuiz) => {
    if (!quizDoc) return;
    saveQuizToDocument?.(quizDoc._id, quiz);
    setQuizOpen(false);
  };

  const removeQuiz = (doc: DocumentItem) => {
    if (!window.confirm("Remove quiz from this document?")) return;
    removeQuizFromDocument?.(doc.id);
  };

  const onReviewClick = async () => {
    const selectedIds = Array.from(selectedDocs);
    const success = await handleReview(selectedIds);
    if (success) {
      setSelectedDocs(new Set());
    }
  };

  const skeletonSx = { bgcolor: isDarkMode ? "grey.800" : "", height: 50 };
  const availableTypes = useMemo(() => {
    if (!pharmacyDocuments) return [];
    const unique = Array.from(
      new Set(pharmacyDocuments.map((d) => d.type).filter(Boolean)),
    );
    return ["all", ...unique];
  }, [pharmacyDocuments]);

  const handleDeleteDocument = async (docId: string) => {
    setDeleteDocId(docId);
    setDeleteOpen(true);
  };

  const confirmDeleteDocument = async () => {
    await handleDeleteCompanyDocuments([deleteDocId]);
    if (!deleteDocId) return;
    setDeleteDocId(null);
    setDeleteOpen(false);
  };

  const handleBulkDelete = () => {
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedDocs.size > 0) {
      await handleDeleteCompanyDocuments(
        [...selectedDocs],
        selectedEmptyFolderIds.size === 0,
      );
    }
    if (selectedEmptyFolderIds.size > 0) {
      await handleDeleteEmptyFolders([...selectedEmptyFolderIds]);
    }
    setSelectedDocs(new Set());
    setSelectedEmptyFolderIds(new Set());
    setBulkDeleteOpen(false);
  };

  const openRenameFolder = (folderName: string, groupDocs: DocumentItem[]) => {
    const folder = folderByName.get(getFolderKey(folderName));
    const folderId =
      folder?._id ||
      folder?.id ||
      groupDocs.find((doc) => doc.folderId)?.folderId ||
      "";

    setRenameFolderId(folderId);
    setRenameFolderName(folderName);
    setRenameFolderOpen(true);
  };

  const saveRenameFolder = async () => {
    const nextName = renameFolderName.trim();
    if (!nextName) return;

    if (!renameFolderId) {
      toast({
        title: "Unable to rename folder",
        description: "Folder id was not found for this row.",
      });
      return;
    }

    setIsRenameSaving(true);
    toast({ title: "Saving" });

    const response = await handleRenameFolder(renameFolderId, nextName);

    setIsRenameSaving(false);
    if (!response) return;

    toast({ title: "Saved" });
    setRenameFolderOpen(false);
    setRenameFolderId("");
    setRenameFolderName("");
  };

  const handleCreateNewFolder = async () => {
    const name = createFolderName.trim();
    if (!name) {
      setCreateFolderError("Folder name is required.");
      return;
    }
    if (folderByName.get(getFolderKey(name))) {
      setCreateFolderError("A folder with this name already exists.");
      return;
    }

    setIsCreatingFolder(true);
    await handleAddFolder(name);
    setFolders((prev) => Array.from(new Set([...prev, name])).sort());
    setIsCreatingFolder(false);
    toast({ title: "Folder created" });
    setCreateFolderOpen(false);
    setCreateFolderName("");
    setCreateFolderError("");
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-950">
      {/* Secondary Tabs - match Patients/Approvals */}
      <div className="px-6 py-3 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex space-x-6">
          {[
            ...(hasRolePermission ? [{ id: "admin", label: "Admin" }] : []),
            { id: "my", label: "My Documents" },
            ...(hasRolePermission
              ? [{ id: "analytics", label: "Analytics" }]
              : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleDocumentTabsClick(tab.id as any)}
              className={`flex items-center gap-2 py-2 text-xs font-medium transition-all duration-200 relative ${
                activeDocumentsTab === (tab.id as any)
                  ? "text-gray-900 dark:text-gray-100"
                  : "text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:text-gray-400 dark:text-gray-500 hover:dark:text-gray-200"
              }`}
            >
              {tab.label}
              {activeDocumentsTab === (tab.id as any) && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Search & Filters (shared between Admin and My Documents) */}
      {(activeDocumentsTab === "admin" || activeDocumentsTab === "my") && (
        <div className="px-6 py-4 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-800">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[280px] max-w-md">
              <div className="relative">
                <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2 dark:text-gray-500" />
                <Input
                  placeholder="Search documents by title, type, or date..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-56">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                {/* <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Filter className="w-3 h-3 text-gray-500 dark:text-gray-400 dark:text-gray-500" />{" "}
                      All Types
                    </div>
                  </SelectItem>
                  <SelectItem value="Referral">Referral</SelectItem>
                  <SelectItem value="Consultation Summary">
                    Consultation Summary
                  </SelectItem>
                  <SelectItem value="Vaccination Certificate">
                    Vaccination Certificate
                  </SelectItem>
                  <SelectItem value="Prescription">Prescription</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent> */}
                <SelectContent>
                  {availableTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t === "all" ? "All Types" : t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeDocumentsTab === "admin" && (
              <div className="w-64">
                {(() => {
                  // Use assignment.email as the filter value (display a friendly name)
                  const members = Array.from(
                    new Set(
                      (pharmacyDocuments || []).flatMap((d) =>
                        d.assignments.map((a) => a.email || ""),
                      ),
                    ),
                  )
                    .filter(Boolean)
                    .sort();

                  return (
                    <Select
                      value={memberFilter}
                      onValueChange={setMemberFilter}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Members</SelectItem>
                        {members?.map((email) => (
                          <SelectItem key={email} value={email}>
                            {displayNameFromEmail(email)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            )}
            {activeDocumentsTab === "admin" && (
              <Button
                variant="outline"
                onClick={() => {
                  setCreateFolderName("");
                  setCreateFolderError("");
                  setCreateFolderOpen(true);
                }}
                className="gap-2 bg-black text-white hover:bg-gray-900 hover:text-white"
              >
                <Plus className="w-4 h-4" /> Add Folder
              </Button>
            )}
            {activeDocumentsTab === "admin" && (
              <Button
                onClick={() => {
                  setCreateOpen(true);
                  setFolder("");
                }}
                className="gap-2"
              >
                <Plus className="w-4 h-4" /> New Document
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 p-6 overflow-auto">
        {/* Admin Tab Content */}
        {activeDocumentsTab === "admin" && (
          <div className="bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
            <Accordion
              type="multiple"
              className="divide-y"
              value={expandedFolders}
              onValueChange={setExpandedFolders}
            >
              {groupedByType.map(([group, groupDocs]) => {
                // Calculate overall compliance for this folder

                const totalAssignments = groupDocs.reduce(
                  (sum, doc) => sum + doc.assignments.length,
                  0,
                );
                const compliantAssignments = groupDocs.reduce(
                  (sum, doc) =>
                    sum + doc.assignments.filter((a) => a.compliant).length,
                  0,
                );
                const folderComplianceRate =
                  totalAssignments > 0
                    ? Math.round(
                        (compliantAssignments / totalAssignments) * 100,
                      )
                    : 0;
                const isFolderGroup =
                  folderByName.has(getFolderKey(group)) ||
                  groupDocs.some((doc) => doc.folder === group);
                const emptyFolderId: string | undefined =
                  isFolderGroup &&
                  !baseDocuments.some(
                    (d) => (d.folder || d.type) === group,
                  )
                    ? folderByName.get(getFolderKey(group))?._id
                    : undefined;

                return (
                  <AccordionItem key={group} value={group}>
                    <div className="flex items-center px-4 py-3">
                      {/* Folder Caret - positioned on the left like document rows */}
                      <button
                        aria-label={
                          expandedFolders.includes(group)
                            ? "Collapse folder"
                            : "Expand folder"
                        }
                        className="p-1 mr-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedFolders((prev) =>
                            prev.includes(group)
                              ? prev.filter((f) => f !== group)
                              : [...prev, group],
                          );
                        }}
                      >
                        {expandedFolders.includes(group) ? (
                          <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </button>

                      <span
                        onClick={(e) => e.stopPropagation()}
                        className="mr-2"
                      >
                        <Checkbox
                          checked={
                            emptyFolderId
                              ? selectedEmptyFolderIds.has(emptyFolderId)
                              : groupDocs.length > 0 &&
                                groupDocs.every((d) => selectedDocs.has(d.id))
                              ? true
                              : groupDocs.some((d) => selectedDocs.has(d.id))
                              ? ("indeterminate" as any)
                              : false
                          }
                          onCheckedChange={(val) => {
                            if (emptyFolderId) {
                              setSelectedEmptyFolderIds((prev) => {
                                const next = new Set(prev);
                                if (val) next.add(emptyFolderId);
                                else next.delete(emptyFolderId);
                                return next;
                              });
                              return;
                            }
                            setSelectedDocs((prev) => {
                              const next = new Set(prev);
                              const ids = groupDocs.map((d) => d.id);
                              const check = !!val;
                              if (check) ids.forEach((id) => next.add(id));
                              else ids.forEach((id) => next.delete(id));
                              return next;
                            });
                          }}
                        />
                      </span>
                      <AccordionTrigger className="flex min-w-0 flex-1 px-0 py-0 font-semibold">
                        <div className="flex w-full min-w-0 items-center gap-3">
                          <span className="inline-flex items-center justify-center w-6 h-6 text-indigo-600 rounded-md bg-indigo-50 dark:bg-indigo-900/40 dark:text-indigo-300">
                            <FolderIcon className="w-4 h-4" />
                          </span>
                          <span className="text-left">{group}</span>

                          {/* Folder Compliance Indicator */}
                          <div className="flex items-center gap-2 ml-4">
                            <Progress
                              value={folderComplianceRate}
                              className="w-24 h-2"
                            />
                            <span
                              className={`text-xs font-medium ${
                                folderComplianceRate >= 80
                                  ? "text-green-600 dark:text-green-400"
                                  : folderComplianceRate >= 50
                                  ? "text-yellow-600 dark:text-yellow-400"
                                  : "text-red-600 dark:text-red-400"
                              }`}
                            >
                              {folderComplianceRate}%
                            </span>
                          </div>

                          <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                            ({groupDocs.length} docs • {compliantAssignments}/
                            {totalAssignments} compliant)
                          </span>
                        </div>
                      </AccordionTrigger>
                      {hasRolePermission && isFolderGroup && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="ml-auto h-7 w-7 shrink-0 p-0"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openRenameFolder(group, groupDocs);
                          }}
                          title="Rename folder"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <AccordionContent>
                      <div className="px-2 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50 dark:bg-gray-900">
                              <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                Title
                              </TableHead>
                              <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                Created
                              </TableHead>
                              <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                Review
                              </TableHead>
                              <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                Size
                              </TableHead>
                              <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                Compliancy
                              </TableHead>
                              <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                Actions
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {groupDocs.map((doc) => (
                              <DocumentRow
                                key={doc.id}
                                doc={doc}
                                isSelected={selectedDocs.has(doc.id)}
                                isExpanded={!!expandedDocs[doc.id]}
                                onToggleExpand={onToggleExpand}
                                onToggleSelect={onToggleSelect}
                                onOpenEdit={openEditCb}
                                onOpenQuiz={openQuizCb}
                                onView={handleView}
                                onDelete={handleDeleteDocument}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {groupedByType.length === 0 && (
              <div className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  No documents found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your search or filters.
                </p>
              </div>
            )}
          </div>
        )}

        {/* My Documents Tab Content */}
        {activeDocumentsTab === "my" && (
          <>
            {isMyDocumentsLoading ? (
              <MyDocumentsSkeleton isDarkMode={isDarkMode} />
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
                <Accordion
                  type="multiple"
                  className="divide-y"
                  value={expandedFolders}
                  onValueChange={setExpandedFolders}
                >
                  {groupedByTypeForMyDocuments.map(([group, groupDocs]) => (
                    <AccordionItem key={group} value={group}>
                      <div className="flex items-center px-4 py-3">
                        {/* Folder Caret - positioned on the left like document rows */}
                        <button
                          aria-label={
                            expandedFolders.includes(group)
                              ? "Collapse folder"
                              : "Expand folder"
                          }
                          className="p-1 mr-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedFolders((prev) =>
                              prev.includes(group)
                                ? prev.filter((f) => f !== group)
                                : [...prev, group],
                            );
                          }}
                        >
                          {expandedFolders.includes(group) ? (
                            <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          )}
                        </button>

                        <AccordionTrigger className="flex-1 px-0 py-0 font-semibold">
                          <div className="flex items-center flex-1 gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 text-indigo-600 rounded-md bg-indigo-50 dark:bg-indigo-900/40 dark:text-indigo-300">
                              <FolderIcon className="w-4 h-4" />
                            </span>
                            <span className="text-left">{group}</span>
                            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                              ({groupDocs.length})
                            </span>
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent>
                        <div className="px-2 pb-3">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50 dark:bg-gray-900">
                                <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                  Title
                                </TableHead>
                                <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                  Assigned Status
                                </TableHead>
                                <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                  Created
                                </TableHead>
                                <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                                  Actions
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupDocs.map((doc) => (
                                <TableRow
                                  key={doc.documentId}
                                  className="hover:bg-gray-50 hover:dark:bg-gray-900"
                                >
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg dark:bg-blue-900/30">
                                        <FileText className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                                      </div>
                                      <div className="space-y-0.5">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-gray-900">
                                            {doc.title}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {doc.compliant ? (
                                      <div className="flex items-center gap-2 text-sm text-green-700">
                                        <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                                        Completed{" "}
                                        {doc.completedAt
                                          ? formatUKDateTime(doc.completedAt)
                                          : ""}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2 text-sm text-yellow-700">
                                        <span className="h-2.5 w-2.5 rounded-full bg-yellow-500" />{" "}
                                        Pending
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {doc.createdOn
                                        ? formatUKDateTime(doc.createdOn)
                                        : "-"}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <Button
                                        size="sm"
                                        className="px-4"
                                        onClick={async () => {
                                          if (onStartSession) {
                                            // Transform raw API data to DocumentItem format
                                            const transformedDocs: DocumentItem[] = (
                                              myDocumentsData?.assignedDocuments ||
                                              []
                                            ).map((pd: any) => {
                                              const quiz =
                                                pd.questions &&
                                                pd.questions.length > 0
                                                  ? {
                                                      questions: pd.questions.map(
                                                        (q: any) => ({
                                                          id:
                                                            q.questionId ||
                                                            `q-${Math.random()
                                                              .toString(36)
                                                              .slice(2, 9)}`,
                                                          question:
                                                            q.question || "",
                                                          options:
                                                            q.options || [],
                                                          correctIndex:
                                                            q.correctIndex || 0,
                                                        }),
                                                      ),
                                                    }
                                                  : undefined;

                                              return {
                                                _id: pd?.documentId,
                                                id: pd?.documentId || "",
                                                title: pd?.title || "",
                                                type: (mapEnumToDocumentType(
                                                  pd?.documentType,
                                                ) ||
                                                  "General") as DocumentItem["type"],
                                                createdAt:
                                                  pd?.createdOn ||
                                                  pd?.createdAt,
                                                sizeKB: pd?.sizeKB || 0,
                                                content: pd?.content || "",
                                                folder:
                                                  pd?.folderName ||
                                                  mapEnumToFolderName(
                                                    pd?.folder,
                                                  ) ||
                                                  undefined,
                                                reviewInterval:
                                                  mapEnumToReviewInterval(
                                                    pd?.reviewInterval,
                                                  ) || undefined,
                                                lastReviewedAt:
                                                  pd?.lastReviewedAt ||
                                                  undefined,
                                                lastReviewedBy:
                                                  pd?.lastReviewedBy ||
                                                  undefined,
                                                fileUrl:
                                                  pd?.mainFile?.fileURL ||
                                                  undefined,
                                                assignments: [
                                                  {
                                                    email:
                                                      hookCurrentUserEmail ||
                                                      currentUserEmail ||
                                                      "",
                                                    compliant: !!pd?.compliant,
                                                    completedAt:
                                                      pd?.completedAt ||
                                                      undefined,
                                                    quizCompleted: !!pd?.quizCompleted,
                                                    quizScore:
                                                      typeof pd?.quizScore ===
                                                      "number"
                                                        ? pd.quizScore
                                                        : undefined,
                                                  },
                                                ],
                                                quiz: quiz,
                                                mainFile: pd?.mainFile,
                                                supportingFiles:
                                                  pd?.supportingFiles,
                                              } as DocumentItem;
                                            });

                                            onStartSession(
                                              transformedDocs,
                                              doc.documentId,
                                            );
                                            try {
                                              await getCompanyDocumentsByCompanyIdAndEmail(
                                                companyId,
                                              );
                                            } catch (e) {
                                              console.error("Error fetching user documents:", e);
                                            }
                                            const details = await fetchDocumentById(
                                              doc.documentId,
                                              doc.mainFile.fileName,
                                              1,
                                            );

                                            if (
                                              details?.base64Content?.startsWith(
                                                "data:",
                                              )
                                            ) {
                                              const mimeType = details.base64Content
                                                .split(",")[0]
                                                .split(":")[1]
                                                .split(";")[0];
                                              const base64Data = details.base64Content.split(
                                                ",",
                                              )[1];

                                              const byteCharacters = atob(
                                                base64Data,
                                              );
                                              const byteNumbers = new Array(
                                                byteCharacters.length,
                                              );
                                              for (
                                                let i = 0;
                                                i < byteCharacters.length;
                                                i++
                                              ) {
                                                byteNumbers[
                                                  i
                                                ] = byteCharacters.charCodeAt(
                                                  i,
                                                );
                                              }
                                              const byteArray = new Uint8Array(
                                                byteNumbers,
                                              );
                                              const blob = new Blob(
                                                [byteArray],
                                                { type: mimeType },
                                              );
                                              const blobUrl = URL.createObjectURL(
                                                blob,
                                              );

                                              setViewingDoc({
                                                title: details.fileName,
                                                url: blobUrl,
                                                mimeType: mimeType,
                                              });
                                            }
                                          }
                                        }}
                                      >
                                        Start
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {filteredMyDocuments.length === 0 && (
                  <div className="py-12 text-center">
                    <User className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="mb-2 text-lg font-medium text-gray-900">
                      {myDocumentsData?.assignedDocuments?.length
                        ? "No documents match your filters"
                        : "No assigned documents"}
                    </h3>
                    <p className="text-gray-600">
                      {myDocumentsData?.assignedDocuments?.length
                        ? "Try adjusting your search or filters."
                        : "You have no documents assigned."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Analytics Tab Content */}
        {activeDocumentsTab === "analytics" && (
          <div className="p-6 bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Documents Analytics
              </h2>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="p-4 border rounded-md dark:border-gray-800">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Total Documents
                </div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {isDocumentsAnalyticsLoading ? (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={80}
                      height={32}
                      className="mb-4"
                      sx={skeletonSx}
                    />
                  ) : (
                    documentsAnalytics?.totalDocuments
                  )}
                </div>
              </div>
              <div className="p-4 border rounded-md dark:border-gray-800">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Assignments
                </div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {isDocumentsAnalyticsLoading ? (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={80}
                      height={32}
                      className="mb-4"
                      sx={skeletonSx}
                    />
                  ) : (
                    documentsAnalytics?.assignments
                  )}
                </div>
              </div>
              <div className="p-4 border rounded-md dark:border-gray-800">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Members
                </div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {isDocumentsAnalyticsLoading ? (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={80}
                      height={32}
                      className="mb-4"
                      sx={skeletonSx}
                    />
                  ) : (
                    documentsAnalytics?.members
                  )}
                </div>
              </div>
              <div className="p-4 border rounded-md dark:border-gray-800">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Overdue Reviews
                </div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {isDocumentsAnalyticsLoading ? (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={80}
                      height={32}
                      className="mb-4"
                      sx={skeletonSx}
                    />
                  ) : (
                    documentsAnalytics?.overdueReviews
                  )}
                </div>
              </div>
            </div>

            {/* Charts section */}
            <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Overall Compliance Donut Chart */}
              <div className="p-4 bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
                <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Overall Compliance
                </h3>
                <ChartContainer
                  config={{
                    compliant: {
                      label: "Compliant",
                      color: "hsl(142, 76%, 36%)",
                    },
                    pending: { label: "Pending", color: "hsl(45, 93%, 47%)" },
                  }}
                  className="h-[240px] sm:h-[280px] w-full"
                >
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Pie
                      data={[
                        {
                          name: "Compliant",
                          value: documentsOverallCompliance?.compliant,
                          fill: "hsl(142, 76%, 36%)",
                        },
                        {
                          name: "Pending",
                          value: documentsOverallCompliance?.pending,
                          fill: "hsl(45, 93%, 47%)",
                        },
                      ]}
                      cx="50%"
                      cy="45%"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={5}
                      dataKey="value"
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      wrapperStyle={{ paddingTop: "20px" }}
                    />
                  </PieChart>
                </ChartContainer>
                <div className="mt-2 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {overallRateCalculated}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Compliance Rate
                  </div>
                </div>
              </div>

              {/* Member Compliance Bar Chart */}
              <div className="p-4 bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
                <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Member Compliance
                </h3>
                <ChartContainer
                  config={{
                    rate: {
                      label: "Compliance Rate",
                      color: "hsl(217, 91%, 60%)",
                    },
                  }}
                  className="h-[260px] sm:h-[320px] w-full"
                >
                  <BarChart
                    data={(() => {
                      const byUser: Record<
                        string,
                        {
                          assigned: number;
                          compliant: number;
                          lastCompleted?: string;
                        }
                      > = {};
                      for (const d of documents) {
                        for (const a of d.assignments) {
                          if (!byUser[a.fullName])
                            byUser[a.fullName] = { assigned: 0, compliant: 0 };
                          byUser[a.fullName].assigned += 1;
                          if (a.compliant) {
                            byUser[a.fullName].compliant += 1;
                            if (a.completedAt) {
                              const prev = byUser[a.fullName].lastCompleted;
                              if (
                                !prev ||
                                +new Date(a.completedAt) > +new Date(prev)
                              ) {
                                byUser[a.fullName].lastCompleted =
                                  a.completedAt;
                              }
                            }
                          }
                        }
                      }
                      return Object.entries(byUser)
                        .map(([fullName, s]) => ({
                          name: displayNameFromEmail(fullName).split(" ")[0],
                          rate: s.assigned
                            ? Math.round((s.compliant / s.assigned) * 100)
                            : 0,
                        }))
                        .sort((a, b) => b.rate - a.rate)
                        .slice(0, 6);
                    })()}
                  >
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 8 }}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                      type="category"
                      tickLine={true}
                      axisLine={true}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      domain={[0, 100]}
                      label={{
                        value: "Compliance %",
                        angle: -90,
                        position: "insideLeft",
                      }}
                      type="number"
                      tickLine={true}
                      axisLine={true}
                      width={50}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="rate"
                      fill="hsl(217, 91%, 60%)"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ChartContainer>
              </div>

              {/* Folder/Type Compliance */}
              <div className="p-4 bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
                <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Folder Compliance
                </h3>
                <ChartContainer
                  config={{
                    rate: {
                      label: "Compliance Rate",
                      color: "hsl(142, 76%, 36%)",
                    },
                  }}
                  className="h-[260px] sm:h-[320px] w-full"
                >
                  <BarChart data={analytics.folders}>
                    <XAxis
                      dataKey="folder"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                      type="category"
                      tickLine={true}
                      axisLine={true}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      domain={[0, 100]}
                      label={{
                        value: "Compliance %",
                        angle: -90,
                        position: "insideLeft",
                      }}
                      type="number"
                      tickLine={true}
                      axisLine={true}
                      width={50}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="rate" radius={[4, 4, 0, 0]}>
                      {analytics.folders.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.rate >= 80
                              ? "hsl(142, 76%, 36%)"
                              : entry.rate >= 50
                              ? "hsl(45, 93%, 47%)"
                              : "hsl(0, 84%, 60%)"
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              </div>
            </div>

            {/* Detailed Tables */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Member Details
                </h3>
                <AnalyticsTable documents={documents} />
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    At-risk Documents
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 dark:bg-gray-900">
                          <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                            Document
                          </TableHead>
                          <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                            Folder
                          </TableHead>
                          <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                            Rate
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.riskyDocs.map((d) => (
                          <TableRow
                            key={d.id}
                            className="hover:bg-gray-50 hover:dark:bg-gray-900"
                          >
                            <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                              {d.title}
                            </TableCell>
                            <TableCell>{d.folder}</TableCell>
                            <TableCell>
                              <span
                                className={`text-sm font-medium ${
                                  d.rate >= 80
                                    ? "text-green-700"
                                    : d.rate >= 50
                                    ? "text-yellow-700"
                                    : "text-red-700"
                                }`}
                              >
                                {d.rate}%
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Review Status
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 text-center border rounded-md dark:border-gray-800">
                      <div className="text-2xl font-bold text-red-600">
                        {analytics.overdueReviews}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Overdue
                      </div>
                    </div>
                    <div className="p-4 text-center border rounded-md dark:border-gray-800">
                      <div className="text-2xl font-bold text-yellow-600">
                        {analytics.dueNext30}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Due Soon
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Floating bulk actions bar (Admin selection) */}
      {activeDocumentsTab === "admin" &&
        selectedDocs.size + selectedEmptyFolderIds.size > 0 && (
        <div className="fixed z-50 -translate-x-1/2 bottom-6 left-1/2">
          <div className="flex items-center gap-4 px-6 py-4 text-gray-900 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-950 dark:border-gray-800 dark:text-gray-100">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              <span className="font-medium">
                {selectedDocs.size + selectedEmptyFolderIds.size} item
                {selectedDocs.size + selectedEmptyFolderIds.size !== 1 ? "s" : ""}{" "}
                selected
              </span>
            </div>
            <div className="w-px h-6 bg-gray-300" />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="text-white bg-emerald-600 hover:bg-emerald-700"
                disabled={selectedDocs.size === 0}
                onClick={() => setReviewOpen(true)}
              >
                Review
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
              </Button>
              <Button
                size="sm"
                className="text-white bg-blue-600 hover:bg-blue-700"
              >
                <Settings className="w-4 h-4 mr-1" /> Process Selected
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedDocs(new Set());
                  setSelectedEmptyFolderIds(new Set());
                }}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark documents as reviewed?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to mark {selectedDocs.size} document
              {selectedDocs.size !== 1 ? "s" : ""} as reviewed.
              <br />
              Reviewer:{" "}
              <strong>{displayNameFromEmail(reviewerEmail || "")}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onReviewClick}
              className="text-white bg-emerald-600 hover:bg-emerald-700"
            >
              Confirm Review
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-md w-[90vw] max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription className="break-words [overflow-wrap:anywhere]">
              Are you sure you want to delete this document? This action cannot
              be undone.
              {deleteDocId && (
                <>
                  <br />
                  <br />
                  <strong className="block break-words [overflow-wrap:anywhere]">
                    {companyDocuments.find((d) => d._id === deleteDocId)?.title}
                  </strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-wrap gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteDocument}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent className="max-w-md w-[90vw] max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedDocs.size + selectedEmptyFolderIds.size} items?
            </AlertDialogTitle>
            <AlertDialogDescription className="break-words [overflow-wrap:anywhere]">
              Are you sure you want to delete {selectedDocs.size} document
              {selectedDocs.size !== 1 ? "s" : ""}
              {selectedEmptyFolderIds.size > 0 &&
                ` and ${selectedEmptyFolderIds.size} empty folder${
                  selectedEmptyFolderIds.size !== 1 ? "s" : ""
                }`}
              ? This action cannot be
              undone.
              <br />
              <br />
              The following documents will be deleted:
              <ul className="list-disc list-outside pl-5 mt-2 max-h-40 overflow-y-auto">
                {Array.from(selectedDocs).map((id) => {
                  const doc = companyDocuments.find((d) => d._id === id);
                  return doc ? (
                    <li
                      key={id}
                      className="text-sm break-words [overflow-wrap:anywhere]"
                    >
                      {doc.title}
                    </li>
                  ) : null;
                })}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-wrap gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete {selectedDocs.size + selectedEmptyFolderIds.size} Item
              {selectedDocs.size + selectedEmptyFolderIds.size !== 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={renameFolderOpen} onOpenChange={setRenameFolderOpen}>
        <AlertDialogContent className="max-w-xl">
          <button
            type="button"
            aria-label="Close"
            className="absolute right-5 top-5 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            onClick={() => setRenameFolderOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl">
              Rename Folder
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label
              htmlFor="rename-folder-name"
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Folder Name
            </label>
            <Input
              id="rename-folder-name"
              value={renameFolderName}
              onChange={(event) => setRenameFolderName(event.target.value)}
              maxLength={500}
              autoFocus
            />
            <CharLimitMessage value={renameFolderName} max={500} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRenameSaving}>
              Close
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isRenameSaving || !renameFolderName.trim()}
              onClick={(event) => {
                event.preventDefault();
                saveRenameFolder();
              }}
              className="bg-black text-white hover:bg-gray-900"
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <AlertDialogContent className="max-w-xl">
          <button
            type="button"
            aria-label="Close"
            className="absolute right-5 top-5 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            onClick={() => setCreateFolderOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl">
              Create Folder
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label
              htmlFor="create-folder-name"
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Folder Name
            </label>
            <Input
              id="create-folder-name"
              value={createFolderName}
              onChange={(event) => {
                setCreateFolderName(event.target.value);
                if (createFolderError) setCreateFolderError("");
              }}
              placeholder="Enter folder name"
              maxLength={500}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleCreateNewFolder();
                }
              }}
            />
            <CharLimitMessage value={createFolderName} max={500} />
            {createFolderError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {createFolderError}
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingFolder}>
              Close
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isCreatingFolder || !createFolderName.trim()}
              onClick={(event) => {
                event.preventDefault();
                handleCreateNewFolder();
              }}
              className="bg-black text-white hover:bg-gray-900"
            >
              Create
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewDocumentModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        folders={folders}
        onAddFolder={(name) =>
          setFolders((prev) => Array.from(new Set([...prev, name])).sort())
        }
      />
      <NewDocumentModal
        open={editOpen}
        onOpenChange={setEditOpen}
        onCreate={() => {}}
        initial={editInitial as any}
        onUpdate={(id, data) => handleUpdateDocument(id, data)}
        folders={folders}
        onAddFolder={(name) =>
          setFolders((prev) => Array.from(new Set([...prev, name])).sort())
        }
      />
      <QuizModal
        open={quizOpen}
        onOpenChange={setQuizOpen}
        onSave={handleSaveQuiz}
        initialQuiz={quizDoc?.quiz as any}
        docTitle={quizDoc?.title || ""}
        docType={quizDoc?.type}
        createdAt={quizDoc?.createdAt}
        sizeKB={quizDoc?.sizeKB}
        initialText={quizDoc?.content || ""}
        docId={quizDoc?._id}
      />
      <ViewDocumentModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        title={viewingDoc?.title}
        documentUrl={viewingDoc?.url}
        loading={viewLoading}
        mimeType={viewingDoc?.mimeType}
      />
    </div>
  );
}

const DocumentRow = React.memo(
  function DocumentRow({
    doc,
    isSelected,
    isExpanded,
    onToggleExpand,
    onToggleSelect,
    onOpenEdit,
    onOpenQuiz,
    onView,
    onDelete,
  }: {
    doc: DocumentItem;
    isSelected: boolean;
    isExpanded: boolean;
    onToggleExpand: (id: string) => void;
    onToggleSelect: (id: string, checked: boolean) => void;
    onOpenEdit: (doc: DocumentItem) => void;
    onOpenQuiz: (doc: DocumentItem) => void;
    onView: (doc: any) => void;
    onDelete: (id: string) => void;
  }) {
    const { downloadDocumentById } = useDocumentsData();

    // Fixed quiz detection - check both quiz.questions and Questions from MongoDB
    const hasQuiz = useMemo(() => {
      return doc.quiz?.questions?.length > 0;
    }, [doc.quiz?.questions]);

    const handleDownload = async (doc: any) => {
      try {
        if (!doc?._id || !doc?.mainFile?.fileName) {
          console.warn("❌ No main file found for this document:", doc);
          return;
        }
        await downloadDocumentById(doc._id, doc.mainFile.fileName, 1);
      } catch (error) {
        console.error("Error during download:", error);
      }
    };

    return (
      <>
        <TableRow className="hover:bg-gray-50 hover:dark:bg-gray-900">
          <TableCell>
            <div className="flex items-center gap-3">
              <button
                aria-label={isExpanded ? "Collapse" : "Expand"}
                className="p-1 rounded hover:bg-gray-100"
                onClick={() => onToggleExpand(doc.id)}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
              <span onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(val) => onToggleSelect(doc.id, !!val)}
                />
              </span>
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg dark:bg-blue-900/30">
                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{doc.title}</span>
                </div>
              </div>
            </div>
          </TableCell>
          <TableCell>
            <span className="text-gray-700 dark:text-gray-300">
              {formatUKDateTime(doc.createdAt)}
            </span>
          </TableCell>
          <TableCell>
            {(() => {
              const last = new Date(doc.lastReviewedAt || doc.createdAt);
              const months = toMonths(doc.reviewInterval);
              const next = months ? addMonths(last, months) : null;
              const reviewer = doc.lastReviewedBy
                ? displayNameFromEmail(doc.lastReviewedBy)
                : null;
              return (
                <div className="text-xs text-gray-600">
                  <div>
                    Last: {formatUKDateTime(last)}
                    {reviewer ? ` • by ${reviewer}` : ""}
                  </div>
                  <div>Next: {next ? formatUKDateTime(next) : "—"}</div>
                </div>
              );
            })()}
          </TableCell>
          <TableCell>
            <span className="text-gray-700 dark:text-gray-300">
              {doc.sizeKB} KB
            </span>
          </TableCell>
          <TableCell>
            {(() => {
              const total = doc.assignments.length;
              const done = doc.assignments.filter((a) => a.compliant).length;
              const rate = total ? Math.round((done / total) * 100) : 0;
              return (
                <div className="min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <Progress value={rate} className="w-32 h-2" />
                    <span className="text-xs text-gray-600">{rate}%</span>
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
                    {done}/{total} compliant
                  </div>
                </div>
              );
            })()}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => onView(doc)}>
                <Eye className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(doc)}
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onOpenEdit(doc)}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={
                  hasQuiz
                    ? "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300"
                    : ""
                }
                onClick={() => onOpenQuiz(doc)}
                title={hasQuiz ? "Quiz added" : "Add quiz"}
              >
                <Sparkles className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                onClick={() => onDelete(doc.id)}
                title="Delete document"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow className="bg-gray-50 dark:bg-gray-900">
            <TableCell colSpan={6}>
              <div className="p-3 bg-white border rounded-md dark:bg-gray-950 dark:border-gray-800">
                <div className="mb-2 text-sm font-medium">Assigned Members</div>
                <div className="divide-y">
                  {doc.assignments.map((a) => (
                    <div
                      key={a.email}
                      className="flex items-center justify-between py-2"
                    >
                      <div className="text-sm text-gray-800 dark:text-gray-200">
                        {displayNameFromEmail(a.email)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            a.compliant ? "bg-green-500" : "bg-yellow-500"
                          }`}
                        />
                        <span
                          className={`text-sm ${
                            a.compliant ? "text-green-700" : "text-yellow-700"
                          }`}
                        >
                          {a.compliant ? "Compliant" : "Pending"}
                        </span>
                        {a.completedAt && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                            {formatUKDateTime(a.completedAt)}
                          </span>
                        )}
                        {hasQuiz ? (
                          <span
                            className={`ml-3 px-2 py-0.5 text-xs rounded-full border ${
                              a.quizCompleted
                                ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800"
                                : "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800"
                            }`}
                          >
                            {a.quizCompleted
                              ? "Quiz completed"
                              : "Quiz pending"}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  },
  (prev, next) =>
    prev.doc === next.doc &&
    prev.isSelected === next.isSelected &&
    prev.isExpanded === next.isExpanded,
);

function AnalyticsTable({ documents }: { documents: DocumentItem[] }) {
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);
  const stats = useMemo(() => {
    const byUser: Record<
      string,
      {
        assigned: number;
        compliant: number;
        lastCompleted?: string;
        email: string;
        fullName?: string;
      }
    > = {};

    for (const d of documents) {
      for (const a of d.assignments) {
        const key = a.email;
        if (!byUser[key]) {
          byUser[key] = {
            assigned: 0,
            compliant: 0,
            email: a.email,
            fullName: a.fullName,
          };
        }

        byUser[key].assigned += 1;

        if (a.compliant) {
          byUser[key].compliant += 1;
          if (a.completedAt) {
            const prev = byUser[key].lastCompleted;
            if (!prev || +new Date(a.completedAt) > +new Date(prev)) {
              byUser[key].lastCompleted = a.completedAt;
            }
          }
        }
      }
    }

    return Object.values(byUser)
      .map((s) => ({
        fullName: s.fullName,
        email: s.email,
        assigned: s.assigned,
        compliant: s.compliant,
        lastCompleted: s.lastCompleted,
        rate: s.assigned ? Math.round((s.compliant / s.assigned) * 100) : 0,
      }))
      .sort((a, b) => b.rate - a.rate || b.compliant - a.compliant);
  }, [documents]);

  const getUserDocuments = (userEmail: string) => {
    const userDocs = documents
      .filter((doc) => doc.assignments.some((a) => a.email === userEmail))
      .map((doc) => {
        const assignment = doc.assignments.find((a) => a.email === userEmail)!;

        // Use same quiz detection logic as DocumentRow
        const hasQuiz = doc.quiz?.questions?.length > 0;
        return {
          ...doc,
          userAssignment: assignment,
          hasQuiz,
        } as any;
      })
      .sort((a, b) => {
        if (a.userAssignment.compliant !== b.userAssignment.compliant) {
          return a.userAssignment.compliant ? 1 : -1;
        }
        return a.title.localeCompare(b.title);
      });

    return userDocs;
  };

  const toggleUserExpansion = (userEmail: string) => {
    setExpandedUsers((prev) =>
      prev.includes(userEmail)
        ? prev.filter((email) => email !== userEmail)
        : [...prev, userEmail],
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg dark:bg-gray-950 dark:border-gray-800">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 dark:bg-gray-900">
            <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
              User
            </TableHead>
            <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
              Assigned
            </TableHead>
            <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
              Compliant
            </TableHead>
            <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
              Compliance Rate
            </TableHead>
            <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
              Last Completed
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stats.map((row) => (
            <React.Fragment key={row.fullName || row.email}>
              <TableRow className="hover:bg-gray-50 hover:dark:bg-gray-900">
                <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex items-center gap-2">
                    <button
                      aria-label={
                        expandedUsers.includes(row.fullName || row.email)
                          ? "Collapse user details"
                          : "Expand user details"
                      }
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => toggleUserExpansion(row.email)}
                    >
                      {expandedUsers.includes(row.email) ? (
                        <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                    {row.fullName || row.email}
                  </div>
                </TableCell>
                <TableCell>{row.assigned}</TableCell>
                <TableCell>{row.compliant}</TableCell>
                <TableCell>
                  <span
                    className={`text-sm font-medium ${
                      row.rate >= 80
                        ? "text-green-700"
                        : row.rate >= 50
                        ? "text-yellow-700"
                        : "text-red-700"
                    }`}
                  >
                    {row.rate}%
                  </span>
                </TableCell>
                <TableCell>
                  {row.lastCompleted
                    ? formatUKDateTime(row.lastCompleted)
                    : "-"}
                </TableCell>
              </TableRow>

              {expandedUsers.includes(row.email) && (
                <TableRow className="bg-gray-50 dark:bg-gray-900">
                  <TableCell colSpan={5}>
                    <div className="p-4 bg-white border rounded-md dark:bg-gray-950 dark:border-gray-800">
                      <div className="mb-3 text-sm font-medium">
                        Document Compliance for{" "}
                        {displayNameFromEmail(row.email)}
                      </div>
                      <div className="space-y-2">
                        {getUserDocuments(row.email).map((doc: any) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-900"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-md dark:bg-blue-900/30">
                                <FileText className="w-3 h-3 text-blue-600 dark:text-blue-300" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {doc.title}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {doc.folder || doc.type} •{" "}
                                  {formatUKDateTime(doc.createdAt)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {doc.userAssignment.compliant ? (
                                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                                  <span className="text-sm font-medium">
                                    Compliant
                                  </span>
                                  {doc.userAssignment.completedAt && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {formatUKDateTime(
                                        doc.userAssignment.completedAt,
                                      )}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                                  <span className="text-sm font-medium">
                                    Non-Compliant
                                  </span>
                                </div>
                              )}
                              {doc.hasQuiz && (
                                <span
                                  className={`px-2 py-0.5 text-xs rounded-full border ${
                                    doc.userAssignment.quizCompleted
                                      ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800"
                                      : "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800"
                                  }`}
                                >
                                  {doc.userAssignment.quizCompleted
                                    ? "Quiz completed"
                                    : "Quiz pending"}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      {stats.length === 0 && (
        <div className="py-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            No analytics available
          </h3>
          <p className="text-gray-600">
            No assignments found to compute analytics.
          </p>
        </div>
      )}
    </div>
  );
}
